/**
 * `/sessions/:id/summary`: "Bilan" — the session's frozen `closing_summary`,
 * read once and shown for good: whatever later imports change, this screen
 * and the e-mail sent to participants read the same numbers forever.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { getSession } from '../../../api/sessions';
import { Button, Skeleton, useToast } from '../../../components/ui';
import { t } from '../../../i18n/fr';
import { SummaryTiles } from '../components/SummaryTiles';
import { sessionErrorMessage } from '../errors';

import type { SessionWithExtras } from '../../../api/types';

/**
 * Render the "Bilan de session" screen.
 *
 * @returns The page element.
 */
export function SummaryPage() {
  const { id } = useParams<{ id: string }>();
  const sessionId = Number(id);
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [session, setSession] = useState<SessionWithExtras | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getSession(sessionId)
      .then((data) => {
        if (!cancelled) {
          setSession(data);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          showToast(sessionErrorMessage(error), 'error');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId, showToast]);

  if (loading) {
    return (
      <div className="pile" aria-hidden="true">
        <Skeleton height="3rem" />
        <Skeleton height="12rem" />
      </div>
    );
  }

  const summary = session?.closing_summary;

  if (!session || !summary) {
    return (
      <div className="bloc">
        <h2>{t('session.summary.title')}</h2>
        <p className="muted">{t('session.summary.encouragementDefault')}</p>
        <Button variant="primary" fullWidth onClick={() => navigate('/')}>
          {t('session.summary.finish')}
        </Button>
      </div>
    );
  }

  return (
    <div className="pile">
      <div className="bloc">
        <h1>{t('session.summary.title')}</h1>
        <p style={{ margin: 0 }}>
          {t('session.summary.lead', {
            recorded: summary.doors_recorded,
            participants: summary.participants,
          })}
        </p>
      </div>

      <div className="bloc">
        <p className="kicker">{t('session.summary.recapKicker')}</p>
        <SummaryTiles summary={summary} />
      </div>

      {summary.doors_released > 0 ? (
        <div className="bloc">
          <p className="kicker">{t('session.summary.releasedKicker')}</p>
          <div className="rendu">
            <p style={{ margin: 0 }}>
              <b>{summary.doors_released}</b> {t('session.summary.tileReleased').toLowerCase()}.
            </p>
          </div>
          <p className="muted" style={{ margin: 'var(--space-2) 0 0' }}>
            {t('session.summary.releasedReminder')}
          </p>
        </div>
      ) : null}

      <div className="bloc">
        <Button variant="primary" fullWidth onClick={() => navigate('/')}>
          {t('session.summary.finish')}
        </Button>
      </div>
    </div>
  );
}
